/**
 * The three states every screen has besides working: loading, empty, and refused.
 *
 * All three are written out in plain words rather than a spinner and a shrug. An empty list that
 * says nothing looks identical to a broken one, and somebody in a car cannot tell the difference
 * between "no clients yet" and "the request failed", which is the difference between carrying on
 * and ringing Alex.
 */
import type { ReactNode } from 'react';
import type { ApiError } from '../api.ts';
import { MEASURE } from './AppShell.tsx';

export function Busy({ what }: { what: string }) {
  return (
    <p className="py-8 text-center text-sm text-navy/60" role="status">
      Loading {what}.
    </p>
  );
}

export function ErrorNotice({
  title,
  message,
  onRetry,
}: {
  title: string;
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div
      className="rounded-xl border border-coral/50 border-l-4 border-l-coral bg-parchment p-4"
      role="alert"
    >
      <h2 className="font-serif text-lg">{title}</h2>
      <p className="mt-1 text-sm">{message}</p>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 inline-flex min-h-11 items-center rounded-lg border border-navy/25 bg-cream px-4 text-sm font-medium desk:min-h-9"
        >
          Try again
        </button>
      ) : null}
    </div>
  );
}

export function EmptyNotice({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="rounded-xl border border-dashed border-navy/25 p-5 text-center">
      <h2 className="font-serif text-lg">{title}</h2>
      {children ? (
        <div className={`mx-auto mt-1 text-sm text-navy/70 ${MEASURE}`}>{children}</div>
      ) : null}
    </div>
  );
}

/**
 * The one refusal that is answered with a link. `docs/identity.md` asks an office person to have
 * signed in recently before a code or a balance is shown, and the server's 403 carries the sign-in
 * path with the return path already inside it. This renders that path and never composes one.
 */
export function StepUpNotice({ error }: { error: ApiError }) {
  return (
    <div className="rounded-xl border border-gold/50 border-l-4 border-l-gold bg-parchment p-4">
      <p className="text-sm">{error.message}</p>
      {error.signInPath ? (
        <a
          href={error.signInPath}
          className="mt-3 inline-flex min-h-12 items-center rounded-lg border border-navy bg-navy px-5 text-base font-medium text-cream desk:min-h-9 desk:px-4"
        >
          Sign in again to see this
        </a>
      ) : null}
    </div>
  );
}

/** Said after something was written down. Green is not in the palette, so sage carries it. */
export function SavedNotice({ children }: { children: ReactNode }) {
  return (
    <p className="rounded-lg border border-sage/60 bg-sage/20 px-3 py-2 text-sm" role="status">
      {children}
    </p>
  );
}
